import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";

// Pointer drawn signature. Fixed internal resolution, scaled by CSS so
// the pad is responsive; emits a PNG data URL (or null when empty).
export function SignaturePad({ onChange, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 3.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B1220";
  }, []);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };

  const down = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className={`relative rounded-lg border bg-white overflow-hidden shadow-[inset_0_2px_6px_rgba(11,18,32,0.06)] transition-colors ${empty ? "border-stone-300" : "border-[#1B4F8A]"}`}>
        <canvas
          ref={canvasRef}
          width={900}
          height={300}
          className="w-full h-auto touch-none cursor-crosshair block"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          onPointerCancel={up}
          data-testid={testid}
        />
        {empty && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400 pointer-events-none">
            <PenLine className="w-5 h-5" />
            <span className="text-sm">Sign here with your mouse, finger or stylus</span>
          </span>
        )}
        <span className="absolute left-8 right-8 bottom-10 border-b border-stone-300 pointer-events-none" />
        <span className="absolute left-8 bottom-4 font-mono text-[10px] tracking-[0.18em] uppercase text-stone-400 pointer-events-none">Signature</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-stone-500">{empty ? "Nothing drawn yet." : "Signature captured. You can redraw it before signing."}</span>
        <button
          type="button"
          onClick={clear}
          disabled={empty}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 disabled:opacity-40 transition-colors"
          data-testid={`${testid}-clear`}
        >
          <Eraser className="w-3.5 h-3.5" /> Clear and start again
        </button>
      </div>
    </div>
  );
}
